import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Check, Copy, KeyRound, Plus, ShieldCheck, Terminal, Trash2 } from "lucide-react";
import { useState } from "react";

import { apiRequest } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  Segmented,
  Select,
  formatRelative,
  useConfirm,
  useToast,
} from "../../ds";
import { ADMIN_ROLES } from "../../shell/role";

interface ApiToken {
  id: string;
  name: string;
  token_prefix: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

const tokensApi = {
  list: () => apiRequest<ApiToken[]>("/api/v1/me/api-tokens"),
  create: (body: { name: string; scopes: string[]; expiresInDays: number }) =>
    apiRequest<ApiToken & { token: string }>("/api/v1/me/api-tokens", { method: "POST", body }),
  revoke: (id: string) => apiRequest<void>(`/api/v1/me/api-tokens/${id}`, { method: "DELETE" }),
};

const SCOPES: Array<{ id: string; label: string; hint: string; adminOnly?: boolean }> = [
  { id: "reports:read", label: "Read reports", hint: "List reports and read drafts" },
  { id: "datasets:read", label: "Read datasets", hint: "See fields and sample rows to build visuals" },
  { id: "drafts:write", label: "Edit drafts", hint: "Create reports and save draft changes (always validated)" },
  { id: "preview", label: "Preview data", hint: "Render draft pages with live data" },
  { id: "publish", label: "Publish (not recommended)", hint: "Lets the AI publish to TVs without a human check", adminOnly: true },
];

function CopyBox({ text, label }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      {label ? <div className="ds-label" style={{ marginBottom: 6 }}>{label}</div> : null}
      <pre className="ds-mono" style={{ margin: 0, padding: "12px 44px 12px 14px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, whiteSpace: "pre-wrap", wordBreak: "break-all", fontSize: 12.5 }}>{text}</pre>
      <Button
        size="sm"
        variant="ghost"
        icon={done ? Check : Copy}
        aria-label="Copy"
        style={{ position: "absolute", right: 6, bottom: 6 }}
        onClick={() => {
          void navigator.clipboard.writeText(text);
          setDone(true);
          window.setTimeout(() => setDone(false), 1500);
        }}
      />
    </div>
  );
}

export function ApiTokensPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.roles.some((r) => ADMIN_ROLES.includes(r));
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const tokens = useQuery({ queryKey: ["api-tokens"], queryFn: tokensApi.list, retry: false });
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [client, setClient] = useState<"claude-code" | "claude-desktop" | "codex">("claude-code");

  const revoke = useMutation({
    mutationFn: tokensApi.revoke,
    onSuccess: () => {
      toast.success("Token revoked", "Any AI tool using it is disconnected immediately.");
      qc.invalidateQueries({ queryKey: ["api-tokens"] });
    },
  });

  const tokenValue = created ?? "<paste your token here>";
  const snippets = {
    "claude-code": `claude mcp add maridive-reports -e MARIDIVE_REPORTS_TOKEN=${tokenValue} -- python "C:/Tools/maridive_reports_mcp.py"`,
    "claude-desktop": JSON.stringify({ mcpServers: { "maridive-reports": { command: "python", args: ["C:/Tools/maridive_reports_mcp.py"], env: { MARIDIVE_REPORTS_TOKEN: tokenValue } } } }, null, 2),
    codex: `# ~/.codex/config.toml\n[mcp_servers.maridive-reports]\ncommand = "python"\nargs = ["C:/Tools/maridive_reports_mcp.py"]\nenv = { MARIDIVE_REPORTS_TOKEN = "${tokenValue}" }`,
  };

  const active = (tokens.data ?? []).filter((t) => !t.revoked_at);

  return (
    <div className="ds-page">
      <PageHeader
        eyebrow="Build with AI"
        title="AI & API access"
        subtitle="Connect Claude (Claude Code or Claude Desktop) or ChatGPT Codex to the platform. The AI can read your datasets and build report drafts — a person always reviews and publishes."
        actions={<Button variant="primary" icon={Plus} onClick={() => setOpen(true)}>Create access token</Button>}
      />

      <div className="ds-grid" style={{ gridTemplateColumns: "minmax(0, 1.25fr) minmax(0, 1fr)", alignItems: "start" }}>
        <div className="ds-stack">
          <Card title="Your tokens" subtitle="Each token is like a password for one AI tool. Revoke it any time." flush>
            <div style={{ height: 8 }} />
            {tokens.isError ? (
              <div style={{ padding: 20 }}>
                <Banner tone="warning" title="Couldn’t load your tokens">Refresh the page. If it keeps happening, the platform service may be restarting.</Banner>
              </div>
            ) : (
              <DataTable<ApiToken>
                rows={active}
                loading={tokens.isLoading}
                rowKey={(t) => t.id}
                empty={<EmptyState icon={KeyRound} title="No tokens yet" description="Create one to connect Claude or Codex." action={<Button variant="primary" icon={Plus} onClick={() => setOpen(true)}>Create access token</Button>} />}
                columns={[
                  { key: "name", header: "Name", cell: (t) => <div><div style={{ fontWeight: 600 }}>{t.name}</div><div className="ds-mono ds-faint">{t.token_prefix}…</div></div> },
                  { key: "scopes", header: "Can", cell: (t) => <div className="ds-row" style={{ flexWrap: "wrap", gap: 4 }}>{t.scopes.map((s) => <Badge key={s} tone={s === "publish" ? "warning" : "neutral"}>{s}</Badge>)}</div> },
                  { key: "used", header: "Last used", cell: (t) => <span className="ds-muted">{t.last_used_at ? formatRelative(t.last_used_at) : "Never"}</span> },
                  { key: "exp", header: "Expires", cell: (t) => <span className="ds-muted">{t.expires_at ? new Date(t.expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "Never"}</span> },
                  {
                    key: "rv",
                    header: "",
                    align: "right",
                    cell: (t) => (
                      <Button size="sm" variant="ghost" icon={Trash2} onClick={async () => { if (await confirm({ title: `Revoke “${t.name}”?`, message: "The AI tool using this token loses access immediately.", confirmLabel: "Revoke", tone: "danger" })) revoke.mutate(t.id); }}>
                        Revoke
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Card>
          <Card title="What an AI can do here">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.9 }}>
              <li>Look at your datasets (fields, sample rows) and query them.</li>
              <li>Create a report and design pages — KPIs, charts, tables, filters, or custom HTML/CSS panels bound to live data.</li>
              <li>Validate every change against the same rules the Publish button uses.</li>
              <li>Show you a live preview link. <strong>You</strong> review and publish.</li>
            </ul>
            <div style={{ marginTop: 12 }}>
              <Banner tone="info" title="Try this prompt">“Using the Maridive Reports tools, build a one-page fleet utilization dashboard from the MOS Utilization dataset: 4 KPIs across the top, utilization by vessel as a bar chart, and a detail table. Validate it and give me the preview link.”</Banner>
            </div>
          </Card>
        </div>

        <Card title="Connect your AI tool" subtitle="One-time setup, about two minutes. Needs Python 3.10+ on your computer.">
          <div className="ds-stack">
            <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
              <li>Create an access token (button above) and copy it.</li>
              <li><a href="/downloads/maridive_reports_mcp.py" download>Download the connector</a> and save it, e.g. to <span className="ds-mono">C:\Tools\</span>.</li>
              <li>Add it to your AI tool with the snippet below.</li>
            </ol>
            <Segmented
              value={client}
              onChange={setClient}
              options={[
                { value: "claude-code", label: "Claude Code", icon: Terminal },
                { value: "claude-desktop", label: "Claude Desktop", icon: Bot },
                { value: "codex", label: "ChatGPT Codex", icon: Terminal },
              ]}
            />
            <CopyBox text={snippets[client]} label={client === "claude-code" ? "Run in a terminal" : client === "claude-desktop" ? "Add to claude_desktop_config.json" : "Add to your Codex config"} />
            <div className="ds-small ds-muted">
              <ShieldCheck size={14} style={{ verticalAlign: "-2px" }} /> The connector talks to <span className="ds-mono">reports.maridive-group.org</span> over HTTPS with your token. Drafts only — nothing reaches TVs until a person publishes.
            </div>
          </div>
        </Card>
      </div>

      <CreateTokenModal open={open} onClose={() => setOpen(false)} isAdmin={isAdmin} onCreated={(t) => { setCreated(t); qc.invalidateQueries({ queryKey: ["api-tokens"] }); }} />
      <Modal open={!!created} onClose={() => setCreated(null)} title="Copy your token now" description="For your security it's shown only once. Store it in your AI tool's settings." footer={<Button variant="primary" onClick={() => setCreated(null)}>I've copied it</Button>}>
        {created ? <CopyBox text={created} /> : null}
      </Modal>
    </div>
  );
}

function CreateTokenModal({ open, onClose, isAdmin, onCreated }: { open: boolean; onClose: () => void; isAdmin: boolean; onCreated: (token: string) => void }) {
  const [name, setName] = useState("Claude on my laptop");
  const [scopes, setScopes] = useState<string[]>(["reports:read", "datasets:read", "drafts:write", "preview"]);
  const [days, setDays] = useState("90");
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => tokensApi.create({ name: name.trim(), scopes, expiresInDays: Number(days) }),
    onSuccess: (t) => {
      onClose();
      onCreated(t.token);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Could not create the token."),
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create access token"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={KeyRound} loading={create.isPending} disabled={!name.trim() || scopes.length === 0} onClick={() => create.mutate()}>Create token</Button>
        </>
      }
    >
      <div className="ds-stack">
        <Field label="Name" hint="So you recognise it later, e.g. “Codex – Ahmed laptop”." error={error ?? undefined}>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div>
          <div className="ds-label" style={{ marginBottom: 8 }}>What this token can do</div>
          <div className="ds-stack" style={{ gap: 8 }}>
            {SCOPES.filter((s) => !s.adminOnly || isAdmin).map((s) => (
              <div key={s.id}>
                <Checkbox label={<strong>{s.label}</strong>} checked={scopes.includes(s.id)} onChange={(v) => setScopes((prev) => (v ? [...prev, s.id] : prev.filter((x) => x !== s.id)))} />
                <div className="ds-small ds-muted" style={{ marginLeft: 24 }}>{s.hint}</div>
              </div>
            ))}
          </div>
        </div>
        <Field label="Expires after">
          <Select value={days} onChange={(e) => setDays(e.target.value)}>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
            <option value="180">180 days</option>
            <option value="365">1 year</option>
          </Select>
        </Field>
      </div>
    </Modal>
  );
}
